
import { GoogleGenAI, Type } from "@google/genai";
import { Language, AppMode } from "../types";

const getAi = () => {
    const apiKey = process.env.API_KEY;
    if (!apiKey) return null;
    return new GoogleGenAI({ apiKey: apiKey });
};

export interface ParsedItem {
    name: string;
    quantity: number;
    unit: string;
    severity?: number; 
}

/**
 * Local non-AI parser for manual text entry.
 * Handles patterns like "2 apples", "200g rice", "1 bowl of soup"
 */
export function parseInputLocally(text: string, mode: AppMode): ParsedItem[] {
    const isFood = mode === 'meal';
    // Split by comma, semi-colon or new line
    const lines = text.split(/[,;\n]/).filter(l => l.trim().length > 0);
    
    return lines.map(line => {
        const trimmed = line.trim();
        
        if (isFood) {
            // Regex to match: [quantity] [unit] [name] or [quantity] [name]
            // Matches: "2", "2.5", "2,5"
            // Potential units: g, ml, kg, cup, serving...
            const qtyRegex = /^(\d+(?:[.,]\d+)?)\s*([a-zA-Z]*)\s+(.*)$/;
            const match = trimmed.match(qtyRegex);

            if (match) {
                const qty = parseFloat(match[1].replace(',', '.'));
                const unit = match[2] || 'unit';
                const name = match[3].trim();
                return { name, quantity: qty, unit };
            }

            // Fallback for just "Apple" -> 1 unit Apple
            return { name: trimmed, quantity: 1, unit: 'unit' };
        } else {
            // Symptom parsing: "Headache 6" or "Stomach pain"
            const severityRegex = /^(.*)\s+(\d+)$/;
            const match = trimmed.match(severityRegex);
            
            if (match) {
                return { name: match[1].trim(), quantity: 1, unit: 'unit', severity: parseInt(match[2]) };
            }
            return { name: trimmed, quantity: 1, unit: 'unit', severity: 5 };
        }
    });
}

/**
 * AI Parser reserved for Audio or complex unstructured text.
 */
export async function parseNaturalLanguageInput(
    text: string, 
    mode: AppMode, 
    language: Language,
    knownNames: string[] = []
): Promise<ParsedItem[]> {
    try {
        const ai = getAi();
        if (!ai) return parseInputLocally(text, mode);

        const isFood = mode === 'meal';
        const knownNamesList = knownNames.length > 0 ? `IMPORTANT: Preserve these exact names if found: ${knownNames.join(', ')}.` : '';
        
        const systemInstruction = language === 'fr' 
            ? `Extrayez une liste d'éléments. ${knownNamesList} NE MODIFIEZ PAS ces noms propres.`
            : `Extract items. ${knownNamesList} DO NOT modify or simplify these specific names.`;

        const schema = {
            type: Type.ARRAY,
            items: {
                type: Type.OBJECT,
                properties: {
                    name: { type: Type.STRING },
                    quantity: { type: Type.NUMBER },
                    unit: { type: Type.STRING },
                    ...(isFood ? {} : { severity: { type: Type.NUMBER } })
                },
                required: ['name', 'quantity', 'unit']
            }
        };

        const response = await ai.models.generateContent({
            model: 'gemini-3-flash-preview',
            contents: text,
            config: {
                systemInstruction: systemInstruction,
                responseMimeType: "application/json",
                responseSchema: schema,
            }
        });

        const result = JSON.parse(response.text || "[]");
        return Array.isArray(result) ? result : [];

    } catch (error) {
        console.error("AI Text parsing failed, falling back to local", error);
        return parseInputLocally(text, mode);
    }
}
